// @author Jayden Scahill - Old Mate Developments

import React, { useState } from 'react';
import { Navigate, Outlet, Link, useLocation } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { 
  LayoutDashboard, 
  Users, 
  Ticket, 
  FileText, 
  Settings,
  LogOut,
  Menu,
  X,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { Button } from '../components/ui/Button';
import { cn } from '../lib/utils';

const navigation = [
  { name: 'Dashboard', href: '/', icon: LayoutDashboard },
  { name: 'Customers', href: '/customers', icon: Users },
  { name: 'Tickets', href: '/tickets', icon: Ticket },
  { name: 'Invoices', href: '/invoices', icon: FileText },
  { name: 'Settings', href: '/settings', icon: Settings },
];

export function DashboardLayout() {
  const { user, signOut } = useAuthStore();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const location = useLocation();

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="min-h-screen bg-dark-900">
      {/* Desktop Sidebar */}
      <div 
        className={cn(
          "hidden lg:fixed lg:inset-y-0 lg:flex lg:flex-col bg-dark-800/50 backdrop-blur-sm border-r border-dark-700 transition-all duration-300 z-30",
          isCollapsed ? "lg:w-16" : "lg:w-64"
        )}
      >
        {/* Logo Section */}
        <div className="flex-1 flex flex-col">
          <div className="h-16 flex items-center px-4 border-b border-dark-700">
            <img 
              src="https://images.squarespace-cdn.com/content/v1/647c3d112b1bdd6e67652190/e464721a-ccc9-4af0-86fb-8fba7f032118/omvs-logo-003-dark.png?t=1739510093111"
              alt="OMTM"
              className="w-8 h-8 object-contain"
            />
            {!isCollapsed && (
              <span className="ml-3 font-semibold text-white">
                OMTM
              </span>
            )}
          </div>

          {/* Navigation */}
          <nav className="flex-1 w-full px-2 py-4 space-y-1">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={cn(
                    'flex items-center px-3 py-2 rounded-lg transition-all duration-200',
                    isActive
                      ? 'bg-primary-500/10 text-primary-500'
                      : 'text-dark-400 hover:bg-dark-800 hover:text-white'
                  )}
                >
                  <item.icon className="h-5 w-5 flex-shrink-0" />
                  {!isCollapsed && (
                    <span className="ml-3 text-sm font-medium">
                      {item.name}
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>

          {/* Collapse Toggle */}
          <div className="p-2 w-full border-t border-dark-700">
            <button
              onClick={() => setIsCollapsed(!isCollapsed)}
              className={cn(
                "w-full flex items-center px-3 py-2 text-dark-400 hover:bg-dark-800 hover:text-white rounded-lg transition-colors",
                "focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-dark-900"
              )}
            >
              {isCollapsed ? (
                <ChevronRight className="h-5 w-5" />
              ) : (
                <>
                  <ChevronLeft className="h-5 w-5" />
                  <span className="ml-3 text-sm font-medium">
                    Collapse
                  </span>
                </>
              )}
            </button>

            {/* Sign Out Button */}
            <button
              onClick={handleSignOut}
              className={cn(
                "w-full flex items-center px-3 py-2 mt-2 text-dark-400 hover:bg-dark-800 hover:text-white rounded-lg transition-colors",
                "focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-dark-900"
              )}
            >
              <LogOut className="h-5 w-5 flex-shrink-0" />
              {!isCollapsed && (
                <span className="ml-3 text-sm font-medium">
                  Sign Out
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <div className="lg:hidden fixed top-0 inset-x-0 z-40 h-16 bg-dark-800/50 backdrop-blur-sm border-b border-dark-700">
        <div className="flex items-center justify-between px-4 h-full">
          <div className="flex items-center">
            <img 
              src="https://images.squarespace-cdn.com/content/v1/647c3d112b1bdd6e67652190/e464721a-ccc9-4af0-86fb-8fba7f032118/omvs-logo-003-dark.png?t=1739510093111"
              alt="OMTM"
              className="w-8 h-8 object-contain"
            />
            <span className="ml-3 font-semibold text-white">
              OMTM
            </span>
          </div>
          <button
            onClick={() => setIsMobileMenuOpen(true)}
            className="text-dark-400 hover:text-white p-2 rounded-lg hover:bg-dark-800"
          >
            <Menu className="h-6 w-6" />
          </button>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div 
            className="fixed inset-0 bg-black/80 backdrop-blur-sm"
            onClick={() => setIsMobileMenuOpen(false)}
          />
          
          <div className="fixed inset-y-0 left-0 w-64 bg-dark-800 border-r border-dark-700">
            <div className="h-16 flex items-center justify-between px-4 border-b border-dark-700">
              <div className="flex items-center">
                <img 
                  src="https://images.squarespace-cdn.com/content/v1/647c3d112b1bdd6e67652190/e464721a-ccc9-4af0-86fb-8fba7f032118/omvs-logo-003-dark.png?t=1739510093111"
                  alt="OMTM"
                  className="w-8 h-8 object-contain"
                />
                <span className="ml-3 font-semibold text-white">
                  OMTM
                </span>
              </div>
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="text-dark-400 hover:text-white p-2 rounded-lg hover:bg-dark-800"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <nav className="px-2 py-4">
              {navigation.map((item) => {
                const isActive = location.pathname === item.href;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={cn(
                      'flex items-center px-3 py-2 rounded-lg transition-all duration-200',
                      isActive
                        ? 'bg-primary-500/10 text-primary-500'
                        : 'text-dark-400 hover:bg-dark-800 hover:text-white'
                    )}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <item.icon className="h-5 w-5 flex-shrink-0" />
                    <span className="ml-3 text-sm font-medium">
                      {item.name}
                    </span>
                  </Link>
                );
              })}
            </nav>

            <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-dark-700">
              <button
                onClick={handleSignOut}
                className="w-full flex items-center justify-center px-4 py-2 text-dark-400 hover:bg-dark-800 hover:text-white rounded-lg transition-colors"
              >
                <LogOut className="h-5 w-4 mr-2" />
                <span className="text-sm font-medium">Sign Out</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className={cn(
        "min-h-screen transition-all duration-300",
        "lg:pl-64 pt-16 lg:pt-0",
        isCollapsed && "lg:pl-16"
      )}>
        <main className="p-4 lg:p-8 max-w-[1920px] mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}